import React, { useState, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight, Play, Pause, Volume2, VolumeX } from 'lucide-react';

// Images des 7 étapes du change debout - Photos réalistes avec même auxiliaire
const CHANGE_DEBOUT_STEPS = [
  {
    id: 1,
    title: "Étape 1 : Préparer le matériel",
    description: "Couche propre, lingettes, tout à portée de main avant d'installer l'enfant",
    image: "https://static.prod-images.emergentagent.com/jobs/6dd9a748-1781-47e8-95b8-945bc0962496/images/c4c77af132fea11e4740d1d734aab3f55fb18033ed3cde99aeec41a758e1895f.png"
  },
  {
    id: 2,
    title: "Étape 2 : Expliquer à l'enfant",
    description: "« Nous allons changer ta couche. Tu vas rester debout et m'aider. »",
    image: "https://static.prod-images.emergentagent.com/jobs/6dd9a748-1781-47e8-95b8-945bc0962496/images/6616c45917d5cb56fb77144774be3b0b0aa519440d17213bb8ccc7c76973650d.png"
  },
  {
    id: 3,
    title: "Étape 3 : Inviter à participer",
    description: "L'enfant baisse son pantalon, tient la barre d'appui, participe activement",
    image: "https://static.prod-images.emergentagent.com/jobs/6dd9a748-1781-47e8-95b8-945bc0962496/images/408d00702e1a98bbaf6e6fea8896dafd1fc59bb06c7fe6009e85714852bbc3b6.png"
  },
  {
    id: 4,
    title: "Étape 4 : Nettoyer",
    description: "Toujours d'avant vers l'arrière, respecter l'intimité, observer la peau",
    image: "https://static.prod-images.emergentagent.com/jobs/6dd9a748-1781-47e8-95b8-945bc0962496/images/90a092af0741325ac053b80163f25728ecc68a06527e58edb07fb292e9765431.png"
  },
  {
    id: 5,
    title: "Étape 5 : Mettre la couche propre",
    description: "L'enfant passe les jambes lui-même, l'aider à ajuster",
    image: "https://static.prod-images.emergentagent.com/jobs/6dd9a748-1781-47e8-95b8-945bc0962496/images/2222f3d074da2125d0d594cd5e2b6f7a66af5435cfb704a73284681d45c5a5d4.png"
  },
  {
    id: 6,
    title: "Étape 6 : Valoriser",
    description: "« Tu as très bien participé ! Bravo ! »",
    image: "https://static.prod-images.emergentagent.com/jobs/6dd9a748-1781-47e8-95b8-945bc0962496/images/70fdf63152873b307ec3a8595657da8d911fd9f0c2651f001708197c726c5279.png"
  },
  {
    id: 7,
    title: "Étape 7 : Lavage des mains",
    description: "Accompagner l'enfant vers le lavabo pour se laver les mains ensemble",
    image: "https://static.prod-images.emergentagent.com/jobs/6dd9a748-1781-47e8-95b8-945bc0962496/images/5ebcbb3a7c84c1924df63e94b3ad51b84115ae834326252b9a97dc8a48ea376d.png"
  }
];

// Logo APDR complet
const APDR_LOGO_FULL = "https://customer-assets.emergentagent.com/job_0bdb7109-8029-4d81-a486-71beb721288a/artifacts/o3wx8hx1_logo%20apdr%20formation.png";

const ChangeDeboutCarousel = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const audioRef = useRef(null);
  const intervalRef = useRef(null);

  // Musique douce de fond
  const BACKGROUND_MUSIC = "https://cdn.pixabay.com/audio/2022/10/25/audio_850edcfd29.mp3";

  useEffect(() => {
    if (isPlaying) {
      intervalRef.current = setInterval(() => {
        setCurrentStep((prev) => (prev + 1) % CHANGE_DEBOUT_STEPS.length);
      }, 5000);
    } else {
      clearInterval(intervalRef.current);
    }
    return () => clearInterval(intervalRef.current);
  }, [isPlaying]);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = 0.3;
      if (isPlaying && !isMuted) {
        audioRef.current.play().catch(() => {});
      } else {
        audioRef.current.pause();
      }
    }
  }, [isPlaying, isMuted]);

  const handlePrev = () => {
    setCurrentStep((prev) => (prev - 1 + CHANGE_DEBOUT_STEPS.length) % CHANGE_DEBOUT_STEPS.length);
  };

  const handleNext = () => {
    setCurrentStep((prev) => (prev + 1) % CHANGE_DEBOUT_STEPS.length);
  };

  const step = CHANGE_DEBOUT_STEPS[currentStep];

  // Couleur thématique - Vert émeraude pour le change debout
  const themeColor = '#10B981';
  const themeColorLight = '#A7F3D0';
  const themeColorDark = '#059669';

  return (
    <div 
      className="relative rounded-2xl overflow-hidden shadow-2xl border-2"
      style={{ minHeight: '480px', borderColor: themeColorLight }}
      data-testid="change-debout-carousel"
    >
      <audio ref={audioRef} loop>
        <source src={BACKGROUND_MUSIC} type="audio/mpeg" />
      </audio>

      {/* Fond avec logo APDR */}
      <div 
        className="absolute inset-0"
        style={{
          backgroundImage: `url(${APDR_LOGO_FULL})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          opacity: 0.08
        }}
      />

      {/* Overlay */}
      <div 
        className="absolute inset-0"
        style={{
          background: `linear-gradient(135deg, rgba(255,255,255,0.92) 0%, rgba(209,250,229,0.88) 100%)`
        }}
      />

      {/* Header */}
      <div 
        className="relative z-10 text-white p-4"
        style={{ background: `linear-gradient(to right, ${themeColor}, ${themeColorDark})` }}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-white p-1 shadow-lg">
              <img src={APDR_LOGO_FULL} alt="APDR" className="w-full h-full object-contain" />
            </div>
            <div>
              <h3 className="font-bold text-lg">Formation APDR de la VAE</h3>
              <p className="text-emerald-100 text-sm">Le change debout - 7 étapes</p>
            </div>
          </div>
          <span className="bg-white/20 px-3 py-1 rounded-full text-sm font-semibold">
            {currentStep + 1} / {CHANGE_DEBOUT_STEPS.length}
          </span>
        </div>
      </div>

      {/* Contenu */}
      <div className="relative z-10 p-6">
        <div className="flex flex-col md:flex-row gap-6 items-center">
          {/* Image */}
          <div className="flex-1 flex justify-center">
            <div className="relative">
              <img 
                src={step.image} 
                alt={step.title}
                className="w-72 h-72 object-cover rounded-2xl shadow-xl border-4 border-white"
                key={step.id}
                style={{ animation: 'fadeIn 0.5s ease-in-out' }}
              />
              <div 
                className="absolute -top-3 -left-3 text-white w-12 h-12 rounded-full flex items-center justify-center font-bold text-xl shadow-lg border-2 border-white"
                style={{ background: `linear-gradient(to bottom right, ${themeColor}, ${themeColorDark})` }}
              >
                {step.id}
              </div>
            </div>
          </div>

          {/* Texte */}
          <div className="flex-1 text-center md:text-left">
            <h2 className="text-2xl font-bold mb-3" style={{ color: themeColor }}>{step.title}</h2>
            <p className="text-gray-700 text-lg mb-6 bg-white/60 p-3 rounded-lg">{step.description}</p>

            {/* Indicateurs */}
            <div className="flex gap-2 justify-center md:justify-start flex-wrap">
              {CHANGE_DEBOUT_STEPS.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentStep(index)}
                  className={`h-3 rounded-full transition-all duration-300`}
                  style={{
                    backgroundColor: index === currentStep ? themeColor : themeColorLight,
                    width: index === currentStep ? '2rem' : '0.75rem'
                  }}
                  data-testid={`change-debout-carousel-dot-${index}`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Contrôles */}
      <div className="relative z-10 bg-white/90 backdrop-blur-sm p-4 flex items-center justify-between border-t" style={{ borderColor: themeColorLight }}>
        <button
          onClick={handlePrev}
          className="flex items-center gap-2 px-4 py-2 rounded-full transition-all font-medium"
          style={{ backgroundColor: `${themeColorLight}40`, color: themeColor }}
          onMouseEnter={(e) => e.target.style.backgroundColor = themeColorLight}
          onMouseLeave={(e) => e.target.style.backgroundColor = `${themeColorLight}40`}
          data-testid="change-debout-carousel-prev"
        >
          <ChevronLeft size={20} />
          Précédent
        </button>

        <div className="flex items-center gap-3">
          <button
            onClick={() => setIsPlaying(!isPlaying)}
            className={`w-14 h-14 rounded-full flex items-center justify-center transition-all shadow-lg`}
            style={{
              backgroundColor: isPlaying ? themeColor : 'white',
              color: isPlaying ? 'white' : themeColor,
              border: isPlaying ? 'none' : `2px solid ${themeColor}`
            }}
            data-testid="change-debout-carousel-play"
          >
            {isPlaying ? <Pause size={24} /> : <Play size={24} className="ml-1" />}
          </button>

          <button
            onClick={() => setIsMuted(!isMuted)}
            className={`w-10 h-10 rounded-full flex items-center justify-center transition-all`}
            style={{
              backgroundColor: isMuted ? '#E5E7EB' : `${themeColorLight}40`,
              color: isMuted ? '#6B7280' : themeColor
            }}
            data-testid="change-debout-carousel-mute"
          >
            {isMuted ? <VolumeX size={18} /> : <Volume2 size={18} />}
          </button>
        </div>

        <button
          onClick={handleNext}
          className="flex items-center gap-2 px-4 py-2 text-white rounded-full transition-all font-medium"
          style={{ backgroundColor: themeColor }}
          onMouseEnter={(e) => e.target.style.backgroundColor = themeColorDark}
          onMouseLeave={(e) => e.target.style.backgroundColor = themeColor}
          data-testid="change-debout-carousel-next"
        >
          Suivant
          <ChevronRight size={20} />
        </button>
      </div>

      {/* Barre de progression */}
      <div className="relative z-10 h-2" style={{ backgroundColor: themeColorLight }}>
        <div 
          className="h-full transition-all duration-300"
          style={{ 
            background: `linear-gradient(to right, ${themeColorLight}, ${themeColor})`,
            width: `${((currentStep + 1) / CHANGE_DEBOUT_STEPS.length) * 100}%` 
          }}
        />
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </div>
  );
};

export default ChangeDeboutCarousel;
